// Admin — Atlas IA · Fiches à créer.
//
// Batch-generation UI: admin picks 1-25 appellations via autocomplete,
// chips display selections, then a single "Générer les fiches" click
// kicks off a background DeepSeek run per appellation. Each result
// lands as a draft bundle in monthly_theme_bundles that the admin
// reviews on /admin/monthly-theme (or here, via the "Voir les bundles"
// links).
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Play, X, Loader2, RefreshCw, ExternalLink } from "lucide-react";
import { api } from "@/pages/admin/inboundApi";

const AdminAtlasFichesPage = () => {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [recent, setRecent] = useState([]);
  const [opts, setOpts] = useState({ include_translations: true, include_producers: false, include_blog: false, include_academy: false });
  const debRef = useRef(null);

  // Load atlas-batch bundles + poll them so admin sees them complete.
  const refreshRecent = async () => {
    try {
      const r = await api("/api/admin/monthly-theme?limit=30");
      const items = (r.items || []).filter((b) => b.source === "atlas-batch");
      setRecent(items);
    } catch (e) { setError(e.message); }
  };
  useEffect(() => {
    refreshRecent();
    const iv = setInterval(refreshRecent, 5000);
    return () => clearInterval(iv);
  }, []);

  // Autocomplete debounce
  useEffect(() => {
    clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      if (!query.trim()) { setSuggestions([]); return; }
      try {
        const r = await api(`/api/admin/inbound/atlas/appellations-autocomplete?q=${encodeURIComponent(query.trim())}`);
        setSuggestions(r.items || []);
      } catch { /* silent */ }
    }, 250);
    return () => clearTimeout(debRef.current);
  }, [query]);

  const addAppellation = (name) => {
    if (!name || selected.includes(name) || selected.length >= 25) return;
    setSelected([...selected, name]);
    setQuery("");
    setSuggestions([]);
    setShowDropdown(false);
  };

  const removeAppellation = (name) => setSelected(selected.filter((s) => s !== name));

  const canGenerate = selected.length > 0 && !busy;

  const generate = async () => {
    setError(""); setBusy(true);
    try {
      const r = await api("/api/admin/inbound/atlas/batch", {
        method: "POST",
        body: JSON.stringify({ appellations: selected, ...opts }),
      });
      window.alert(`✅ ${r.count} génération(s) lancée(s) en arrière-plan.`);
      setSelected([]);
      refreshRecent();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const statusColor = (s) => ({
    queued: "bg-yellow-100 text-yellow-700",
    running: "bg-sky-100 text-sky-700",
    done: "bg-emerald-100 text-emerald-700",
    published: "bg-blue-100 text-blue-700",
    failed: "bg-red-100 text-red-700",
  })[s] || "bg-gray-100 text-gray-700";

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="admin-atlas-fiches">
      <div className="flex items-center gap-3 mb-6">
        <MapPin size={26} className="text-[color:var(--gv-pink)]" />
        <h1 className="text-3xl font-serif">Atlas IA · Fiches à créer</h1>
      </div>

      <p className="text-[13.5px] text-[color:var(--gv-ink)]/60 mb-6 max-w-2xl">
        Sélectionne 1 à 25 appellations, lance la génération et récupère les fiches en brouillon. Chaque
        fiche générée respecte strictement le schéma existant (public_appellations, public_cepages, etc.)
        et arrive en brouillon pour validation manuelle.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-[5px] text-[13px] mb-4" data-testid="atlas-error">{error}</div>
      )}

      {/* Search + chips */}
      <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-5 mb-8 shadow-sm">
        <label className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 block mb-1.5">Appellations à générer ({selected.length}/25)</label>

        <div className="flex flex-wrap gap-1.5 mb-3 min-h-[32px]" data-testid="atlas-chips">
          {selected.map((s) => (
            <span key={s} className="inline-flex items-center gap-1 bg-[color:var(--gv-pink)]/10 text-[color:var(--gv-pink)] px-2.5 py-1 rounded-full text-[13px]" data-testid={`chip-${s}`}>
              {s}
              <button onClick={() => removeAppellation(s)} className="hover:bg-white/50 rounded-full p-0.5"><X size={12} /></button>
            </span>
          ))}
        </div>

        <div className="relative">
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setShowDropdown(true); }}
            onFocus={() => setShowDropdown(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && query.trim()) { e.preventDefault(); addAppellation(query.trim()); }
            }}
            placeholder="Rechercher une appellation (ex. Chablis, Sancerre, Barolo…)"
            className="w-full border border-[color:var(--gv-line)] rounded-[5px] px-3 py-2.5 text-[14px] focus:border-[color:var(--gv-pink)] focus:outline-none"
            data-testid="appellation-input"
          />
          {showDropdown && suggestions.length > 0 && (
            <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-[color:var(--gv-line)] rounded-[5px] shadow-lg max-h-64 overflow-y-auto">
              {suggestions.map((s, i) => (
                <button
                  key={`${s.name}-${i}`}
                  onClick={() => addAppellation(s.name)}
                  className="w-full text-left px-3 py-2 hover:bg-[color:var(--gv-mist)] text-[13.5px] flex items-center justify-between"
                  data-testid={`suggestion-${s.name}`}
                >
                  <span>{s.name}</span>
                  {s.region && <span className="text-[11px] text-[color:var(--gv-ink)]/50">{s.region}</span>}
                </button>
              ))}
              {query.trim() && !suggestions.find((s) => s.name.toLowerCase() === query.trim().toLowerCase()) && (
                <button onClick={() => addAppellation(query.trim())} className="w-full text-left px-3 py-2 hover:bg-[color:var(--gv-mist)] text-[13.5px] text-[color:var(--gv-pink)] border-t border-[color:var(--gv-line)]" data-testid="add-custom">
                  + Ajouter « {query.trim()} »
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-4 mt-5 mb-5 text-[13px]">
          {[
            ["include_translations", "Traductions EN/ES/PT"],
            ["include_producers", "Producteurs"],
            ["include_blog", "Articles blog"],
            ["include_academy", "Cours Academy"],
          ].map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={opts[k]} onChange={(e) => setOpts({ ...opts, [k]: e.target.checked })} className="accent-[color:var(--gv-pink)]" data-testid={`opt-${k}`} />
              {l}
            </label>
          ))}
        </div>

        <button
          onClick={generate}
          disabled={!canGenerate}
          className="inline-flex items-center gap-2 bg-[color:var(--gv-pink)] hover:opacity-90 disabled:opacity-40 text-white px-5 py-2.5 rounded-[5px] text-[14px] font-medium"
          data-testid="generate-batch-btn"
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Play size={14} />}
          Générer les fiches ({selected.length})
        </button>
      </div>

      {/* Recent atlas-batch bundles */}
      <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-5" data-testid="atlas-recent">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl text-[color:var(--gv-ink)]">Générations récentes</h2>
          <button onClick={refreshRecent} className="text-[color:var(--gv-ink)]/55 hover:text-[color:var(--gv-pink)]"><RefreshCw size={14} /></button>
        </div>
        {recent.length === 0 ? (
          <div className="text-[13px] text-[color:var(--gv-ink)]/55">Aucune génération encore. Sélectionne des appellations ci-dessus.</div>
        ) : (
          <ul className="divide-y divide-[color:var(--gv-line)]">
            {recent.map((b) => (
              <li key={b.bundle_id} className="py-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[14px] truncate">{b.theme}</div>
                  <div className="text-[11.5px] text-[color:var(--gv-ink)]/55 mt-0.5 flex items-center gap-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-widest ${statusColor(b.status)}`}>{b.status}</span>
                    {b.step && b.status === "running" && <span>{b.step} · {b.progress}/{b.total_steps}</span>}
                    <span>{b.created_at?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                </div>
                <Link to={`/admin/monthly-theme?bundle=${b.bundle_id}`} className="text-[color:var(--gv-pink)] hover:underline text-[12.5px] inline-flex items-center gap-1" data-testid={`open-${b.bundle_id}`}>
                  Ouvrir <ExternalLink size={11} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminAtlasFichesPage;
